/**
 * Hoodrank API response shapes (served by the Express backend).
 */

export type RankUser = {
  id: string;
  username: string;
  display_name: string;
  description?: string;
  profile_image_url?: string;
  profile_background_image_url?: string;
  location?: string;
  tweets_count?: number;
  bio_urls?: string[];
  followers_count: number;
  followings_count?: number;
  verified: boolean;
  protected: boolean;
  created_at?: string;
};

/** Public Hoodrank API endpoints powering the UI. */
export const API_ENDPOINTS: {
  method: "GET";
  path: string;
  description: string;
}[] = [
  {
    method: "GET",
    path: "/api/rank/:handle",
    description:
      "Full rank profile: user, score, deltas, follower stats and top lists.",
  },
  {
    method: "GET",
    path: "/api/preview",
    description: "Landing preview card for a featured handle.",
  },
  {
    method: "GET",
    path: "/api/trending",
    description: "Ranked index of the highest-signal tracked handles.",
  },
  {
    method: "GET",
    path: "/api/profile/:handle/followers",
    description: "Paginated follower list for the profile tabs.",
  },
  {
    method: "GET",
    path: "/api/profile/:handle/following",
    description: "Paginated following list for the profile tabs.",
  },
  {
    method: "GET",
    path: "/api/profile/:handle/verified-followers",
    description: "Verified accounts following the handle.",
  },
  {
    method: "GET",
    path: "/api/profile/:handle/new-followers-7d",
    description: "Crypto-indexed new followers in the last 7 days.",
  },
  {
    method: "GET",
    path: "/api/profile/:handle/new-following-7d",
    description: "Crypto-indexed new follows in the last 7 days.",
  },
  {
    method: "GET",
    path: "/api/profile/:handle/about",
    description: "Country and username-change metadata for the Trust tab.",
  },
  {
    method: "GET",
    path: "/api/feed/:handle/tweets",
    description: "Recent posts for the feed page.",
  },
  {
    method: "GET",
    path: "/api/feed/:handle/mentions",
    description: "Mentions of the handle for the feed page.",
  },
  {
    method: "GET",
    path: "/api/search/users",
    description: "User search / autocomplete.",
  },
  {
    method: "GET",
    path: "/api/stats",
    description: "Landing receipts and footer block height.",
  },
];

export type Follower = RankUser & {
  /** ISO date when the follow started. */
  followerDate?: string;
  /** Reputation score of this follower, when available. */
  score?: number;
};

export type Score = { score: number };

export type ScoreChanges = {
  week_delta: number;
  month_delta: number;
};

export type FollowersStats = {
  followers_count: number;
  influencers_count: number;
  projects_count: number;
  venture_capitals_count: number;
  user_protected: boolean;
};

export type Tier = "elite" | "strong" | "watch";

export type RankProfile = {
  user: RankUser;
  score: Score;
  scoreChanges: ScoreChanges;
  followersStats: FollowersStats;
  topFollowers: Follower[];
  topFollowing: Follower[];
  /** Indexed accounts that followed in the last 7 days (bundled with rank). */
  newFollowers7d?: Follower[];
  /** Trust metadata from /about (bundled with rank). */
  about?: About | null;
  tier: Tier;
  tierLabel: string;
};

export type TrendingAccount = {
  rank: number;
  user: RankUser;
  score: number;
  /** 0-100 derived: influencers / categorized followers */
  signalRatio: number;
  weekDelta: number;
  monthDelta: number;
  tier: Tier;
};

/** GET /api/stats — landing Receipts + footer block. */
export type SiteStats = {
  handlesRanked: number;
  keyFollowers: number;
  uptimePct: number;
  lastBlock: number | null;
};

export type About = {
  country?: string;
  account_age_days: number;
  username_changes: { username: string; changed_at: string }[];
  is_business_account: boolean;
  bot_likelihood: number; // 0-100
  is_blue_verified?: boolean;
  premium_start_at?: string | null;
  source?: string | null;
};

export type Tweet = {
  id: string;
  text: string;
  created_at: string;
  likes: number;
  retweets: number;
  replies: number;
  views: number;
};

export type Mention = {
  id: string;
  author: RankUser;
  text: string;
  created_at: string;
  likes: number;
  retweets: number;
};

export type Page<T> = {
  items: T[];
  page: number;
  page_size: number;
  total: number;
  has_more: boolean;
};

/* ------------------------------ helpers ------------------------------ */

/**
 * Scores are unbounded reputation points (e.g. 1831).
 * The progress bar is relative to a high watermark.
 * Legacy 0–3 scale values are still formatted correctly if the API returns them.
 */
export const SCORE_HIGH_WATERMARK = 3000;

function isLegacyScale(score: number) {
  return Math.abs(score) <= 3.5;
}

export function scorePercent(score: number, reference?: number) {
  const defaultRef = isLegacyScale(score) ? 3 : SCORE_HIGH_WATERMARK;
  const ref = Math.max(reference ?? defaultRef, score, 1);
  return Math.max(2, Math.min(100, (score / ref) * 100));
}

export function tierFromScore(score: number): Tier {
  if (isLegacyScale(score)) {
    if (score >= 2.2) return "elite";
    if (score >= 1.2) return "strong";
    return "watch";
  }
  if (score >= 1_500) return "elite";
  if (score >= 600) return "strong";
  return "watch";
}

export function tierLabel(tier: Tier) {
  return tier === "elite"
    ? "Elite tier · top 1%"
    : tier === "strong"
      ? "Strong tier · top 8%"
      : "Watch tier · building";
}

export function formatScore(score: number) {
  if (Math.abs(score) < 10) return score.toFixed(2);
  return Math.round(score).toLocaleString("en-US");
}

export function formatDelta(value: number) {
  const abs = Math.abs(value);
  return abs < 1 ? abs.toFixed(2) : Math.round(abs).toLocaleString("en-US");
}

export function categorizedShares(stats: FollowersStats) {
  const total =
    stats.influencers_count +
    stats.projects_count +
    stats.venture_capitals_count;
  if (total <= 0) return null;
  return {
    total,
    influencers: (stats.influencers_count / total) * 100,
    projects: (stats.projects_count / total) * 100,
    vcs: (stats.venture_capitals_count / total) * 100,
  };
}

export function compactCount(n: number) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return String(n);
}

export function normalizeHandle(input: string) {
  return input
    .trim()
    .replace(/^https?:\/\/(x|twitter)\.com\//i, "")
    .replace(/^@/, "")
    .replace(/\/.*$/, "")
    .toLowerCase();
}

export function formatJoined(iso?: string) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}
